"use client";
import ProductCard from "@/components/Product/product-card";
import { message, Pagination, Spin } from "antd";
import React, { useState, useCallback, useEffect } from "react";
import { fetchProductByCategoryData, fetchProductData } from "@/lib/api/product";
import { useTranslations } from "next-intl";
import CompareList from "../CompareList";
import MyEmpty from "../MyEmpty";
import { useBreakpointColumns } from "@/lib/store/breakpointColumnsObj";

const ProductList = ({
	products,
	locale,
	channel,
	slug,
}: {
	products: any;
	locale: string;
	channel: string;
	slug?: string;
}) => {
	const [currentPage, setCurrentPage] = useState(1);
	const [loading, setLoading] = useState(false);
	// 初始化 productsList：对于分类页面在 category.products，普通页面直接是 products
	const initialProductsList = slug 
		? (products as any)?.category?.products || products
		: products;
	const [productsList, setProductsList] = useState(initialProductsList);
	const [cursors, setCursors] = useState<string[]>(['']); // 存储每一页的 cursor，第一页为空字符串
	// 初始化 totalCount：对于分类页面在 category.products.totalCount，普通页面在 products.totalCount
	const initialTotalCount = slug 
		? (products as any)?.category?.products?.totalCount || products?.totalCount || 0
		: products?.totalCount || 0;
	const [totalCount, setTotalCount] = useState(initialTotalCount);
	const t = useTranslations();
	let { breakpointColumns, col } = useBreakpointColumns();

	// 每页显示的产品数量
	const pageSize = 9;

	// 获取指定页的数据
	const fetchPageData = useCallback(async (page: number) => {
		try {
			setLoading(true);
			let res = null;
			let cursor = '';

			// 如果已经有缓存的 cursor，直接使用
			if (cursors[page - 1] !== undefined) {
				cursor = cursors[page - 1];
			} else {
				// 如果没有缓存的 cursor，需要从已知的最后一页开始逐步获取到目标页
				const maxCachedPage = cursors.length;
				if (page > maxCachedPage) {
					// 从最后一页的 cursor 开始获取
					let currentCursor = cursors[maxCachedPage - 1] || '';
					for (let i = maxCachedPage; i < page; i++) {
						if (slug) {
							res = (await fetchProductByCategoryData({ slug, locale, channel, after: currentCursor, first: pageSize }))?.category;
						} else {
							res = await fetchProductData({ locale, channel, after: currentCursor });
						}
						if (res?.products?.pageInfo?.endCursor) {
							currentCursor = res.products.pageInfo.endCursor;
							setCursors(prev => [...prev, currentCursor]);
						} else {
							break;
						}
					}
					cursor = currentCursor;
				} else {
					cursor = cursors[page - 1] || '';
				}
			}

			// 获取当前页数据
			if (slug) {
				res = (await fetchProductByCategoryData({ slug, locale, channel, after: cursor, first: pageSize }))?.category;
			} else {
				res = await fetchProductData({ locale, channel, after: cursor });
			}

			// 处理返回的数据结构
			const productsData = slug ? res?.category?.products : res?.products;
			
			if (productsData) {
				setProductsList(productsData);
				setTotalCount(productsData.totalCount || 0);
				
				// 缓存下一页的 cursor
				if (productsData.pageInfo?.endCursor && cursors[page] === undefined) {
					setCursors(prev => {
						const newCursors = [...prev];
						newCursors[page] = productsData.pageInfo.endCursor;
						return newCursors;
					});
				}
			}
		} catch (e: any) {
			message.error(e.message || t("common.Network349") || "Failed to load products");
		} finally {
			setLoading(false);
		}
	}, [locale, channel, slug, cursors, t]);

	// 处理分页变化
	const handlePageChange = useCallback((page: number) => {
		setCurrentPage(page);
		fetchPageData(page);
		// 滚动到顶部
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, [fetchPageData]);

	// 初始化时设置总数
	useEffect(() => {
		if (products?.totalCount) {
			setTotalCount(products.totalCount);
		}
	}, [products]);

	return (
		<>
			{loading ? (
				<div className="flex justify-center items-center py-20">
					<Spin size="large" />
				</div>
			) : (
				<>
					{!productsList?.edges?.length && (
						<MyEmpty text={""} description={t("nav.Nogoods")} className="py-20 max-md:py-4">
							<i className="ri-shopping-cart-2-fill text-4xl  !text-ddd"></i>
						</MyEmpty>
					)}
					{/* @ts-ignore */}
					<div key={col} className={`grid grid-cols-${col} gap-6 max-md:gap-4 max-md:grid-cols-1`}>
						{productsList?.edges?.map((item: any) => (
							<ProductCard key={item.node.slug} productItem={item.node} locale={locale} twoCol={col==2} />
						))}
					</div>
					
					{productsList?.edges?.length > 0 && totalCount > pageSize && (
						<div className="mt-8 flex justify-center">
							<Pagination
								current={currentPage}
								total={totalCount}
								pageSize={pageSize}
								onChange={handlePageChange}
								showSizeChanger={false}
								className="pagination-custom"
							/>
						</div>
					)}
				</>
			)}

			<div className="max-md:hidden">
				<CompareList />
			</div>
		</>
	);
};

export default ProductList;
